import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, ShoppingBag, Ruler, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { api, rupee, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { ProductCard } from "@/components/ProductCard";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setSize(r.data.sizes?.[0]?.size || null);
      setColor(r.data.colors?.[0] || null);
      setActiveImg(0);
      // Canonicalize URL if opened via UUID
      if (r.data.slug && r.data.slug !== slug && window.history?.replaceState) {
        window.history.replaceState(null, "", `/product/${r.data.slug}`);
      }
    }).catch(() => setP(null));
    api.get(`/products/${slug}/similar`).then((r) => setSimilar(r.data)).catch(() => {});
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-muted-foreground">Loading…</div>;

  const images = (p.images?.length ? p.images : ["https://images.unsplash.com/photo-1617134471130-4186de7a137f?w=800&q=80"]).map(fileUrl);
  const selectedSize = p.sizes?.find((s) => s.size === size);
  const price = selectedSize?.price || p.min_price;
  const inStock = selectedSize ? selectedSize.stock > 0 : true;
  const wished = has(p.id);

  const specs = [
    ["Fabric", p.fabric], ["Color", (p.colors || []).join(", ")], ["Net Quantity", p.net_quantity],
    ["Pattern", p.pattern], ["Length", p.length], ["Neck", p.neck], ["Sleeve Length", p.sleeve],
    ["Padding", p.padding], ["Add-on", p.addon], ["Brand", p.brand], ["Generic Name", p.generic_name],
    ["Country of Origin", p.country_of_origin],
  ].filter(([, v]) => v);

  const add = (buyNow) => {
    if (p.sizes?.length && !size) return toast.error("Please select a size");
    addItem({ product_id: p.id, name: p.name, code: p.code, size: size || "", color: color || "", price, qty, image: images[0] });
    if (buyNow) window.location.href = "/checkout";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div data-aos="fade-right">
          <div className="relative rounded-2xl overflow-hidden bg-secondary aspect-[3/4]">
            <img src={images[activeImg]} alt={p.name} className="w-full h-full object-cover" data-testid="pdp-main-image" />
            {images.length > 1 && (
              <>
                <NavBtn side="left" onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} />
                <NavBtn side="right" onClick={() => setActiveImg((i) => (i + 1) % images.length)} />
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-20 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-primary" : "border-transparent"}`} data-testid={`pdp-thumb-${i}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div data-aos="fade-left">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold">{p.category} · {p.audience}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-1">{p.name}</h1>
          {p.code && <p className="text-xs text-muted-foreground font-mono mt-1">Code: {p.code}</p>}

          <div className="flex items-center gap-3 mt-4">
            <span className="font-mono text-3xl font-bold text-foreground" data-testid="pdp-price">{rupee(price)}</span>
            {p.mrp > price && (
              <>
                <span className="text-muted-foreground line-through font-mono">{rupee(p.mrp)}</span>
                <span className="text-primary font-semibold text-sm">{Math.round((p.mrp - price) / p.mrp * 100)}% off</span>
              </>
            )}
          </div>
          <p className="text-xs text-emerald-600 mt-1">Inclusive of all taxes</p>

          {p.description && <p className="text-sm text-muted-foreground mt-5 leading-relaxed">{p.description}</p>}

          {/* Color */}
          {p.colors?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-2">Color: <span className="text-muted-foreground font-normal">{color}</span></p>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`pdp-color-${c}`}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${color === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Select Size</p>
                {p.size_chart_image && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-sm text-primary inline-flex items-center gap-1 font-medium" data-testid="size-chart-btn"><Ruler className="w-4 h-4" /> Size Chart</button>
                    </DialogTrigger>
                    <DialogContent className="bg-white max-w-lg">
                      <DialogHeader><DialogTitle>Size Chart</DialogTitle></DialogHeader>
                      <img src={fileUrl(p.size_chart_image)} alt="Size chart" className="w-full rounded-xl" />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s.size} onClick={() => setSize(s.size)} disabled={s.stock === 0} data-testid={`pdp-size-${s.size}`}
                    className={`min-w-[52px] px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors disabled:opacity-40 disabled:line-through ${size === s.size ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                    {s.size}
                    <span className="block text-[10px] font-mono opacity-80">{rupee(s.price)}</span>
                  </button>
                ))}
              </div>
              {!inStock && <p className="text-sm text-destructive mt-2">Selected size is out of stock</p>}
            </div>
          )}

          {/* Qty + actions */}
          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-border rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-mono font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => toggle(p.id)} className="w-12 h-12 rounded-full border border-border flex items-center justify-center hover:border-primary transition-colors" data-testid="pdp-wishlist-btn" aria-label="Wishlist">
              <Heart className={`w-5 h-5 ${wished ? "fill-primary text-primary" : ""}`} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            <button onClick={() => add(false)} disabled={!inStock} className="flex-1 inline-flex items-center justify-center gap-2 border border-foreground text-foreground py-4 text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-[hsl(var(--ivory))] transition-colors duration-300 disabled:opacity-50" data-testid="add-to-cart-btn">
              <ShoppingBag className="w-4 h-4" /> Add to Bag
            </button>
            <button onClick={() => add(true)} disabled={!inStock} className="flex-1 bg-primary text-primary-foreground py-4 text-xs uppercase tracking-[0.2em] hover:bg-foreground transition-colors duration-300 disabled:opacity-50" data-testid="buy-now-btn">
              Buy Now
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-5">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> Made in India · Quality assured
          </div>

          {/* Specs */}
          {specs.length > 0 && (
            <div className="mt-8 border-t border-border pt-6">
              <h3 className="font-semibold mb-4">Product Details</h3>
              <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                {specs.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-muted-foreground text-xs uppercase tracking-wide">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      {similar.length > 0 && (
        <div className="mt-20" data-aos="fade-up">
          <h2 className="font-display text-3xl sm:text-4xl font-light mb-8">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {similar.map((s, i) => (
              <div key={s.id} data-aos="fade-up" data-aos-delay={(i % 4) * 120}><ProductCard p={s} /></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const NavBtn = ({ side, onClick }) => (
  <button onClick={onClick} className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-3" : "right-3"} w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow`} data-testid={`gallery-${side}`}>
    {side === "left" ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
  </button>
);
